import { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  Chip,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Pagination,
  Tooltip,
} from "@mui/material";
import {
  PictureAsPdf as PdfIcon,
  LibraryBooks as LibraryBooksIcon,
  Refresh as RefreshIcon,
  OpenInNew as OpenInNewIcon,
  AutoAwesome as AutoAwesomeIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import api from "../../api";
import ConvertToMcqDialog from "./components/ConvertToMcqDialog";

const getStatusLabel = (status) => {
  const map = {
    uploaded: "Uploaded",
    processing: "Processing",
    preview: "Ready-For-Review",
    completed: "Completed",
    failed: "Failed",
    confirmed: "Confirmed",
  };
  return map[status] || status || "Unknown";
};

const statusColors = {
  uploaded: { bg: "#fff3e0", color: "#e65100" },
  processing: { bg: "#fff3e0", color: "#e65100" },
  preview: { bg: "#e3f2fd", color: "#0d47a1" },
  completed: { bg: "#e8f5e9", color: "#1b5e20" },
  confirmed: { bg: "#e8f5e9", color: "#1b5e20" },
  failed: { bg: "#ffebee", color: "#b71c1c" },
};

const normalizeCategoryName = (job) =>
  job.category?.name || job.category_name || "";

const QuestionBankRecentImports = () => {
  const navigate = useNavigate();
  const [imports, setImports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("actionable");
  const [subjectFilter, setSubjectFilter] = useState("");
  const [subjects, setSubjects] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [searchDebounced, setSearchDebounced] = useState("");
  const [page, setPage] = useState(1);
  const [perPage] = useState(15);
  const [convertTarget, setConvertTarget] = useState(null);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [letterFilter, setLetterFilter] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(searchInput), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [searchDebounced, statusFilter, subjectFilter, letterFilter]);

  useEffect(() => {
    api
      .get("/admin/master-form/subjects", { params: { per_page: 100 } })
      .then((res) => {
        const list = res.data?.data?.data || res.data?.data || [];
        setSubjects(Array.isArray(list) ? list.filter((s) => s.key) : []);
      })
      .catch(() => {});
  }, []);

  const fetchImports = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = { page, per_page: perPage };
      const q = searchDebounced.trim();
      if (q) params.search = q;
      if (statusFilter === "actionable") {
        params.status = "preview,completed,confirmed";
      } else if (statusFilter !== "all") {
        params.status = statusFilter;
      }
      if (letterFilter) params.category_letter = letterFilter;
      if (subjectFilter) params.subject = subjectFilter;
      const res = await api.get("/admin/question-bank/import/recent", { params });
      const payload = res.data?.data;
      if (payload && Array.isArray(payload.data)) {
        setImports(payload.data);
        setLastPage(Math.max(1, payload.last_page ?? 1));
        setTotal(payload.total ?? 0);
      } else if (Array.isArray(payload)) {
        setImports(payload);
        setLastPage(1);
        setTotal(payload.length);
      } else {
        setImports([]);
        setLastPage(1);
        setTotal(0);
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load recent imports.");
    } finally {
      setLoading(false);
    }
  }, [page, perPage, searchDebounced, statusFilter, subjectFilter, letterFilter]);

  useEffect(() => {
    fetchImports();
  }, [fetchImports]);

  const canOpenQuestionBank = (job) =>
    ["preview", "completed", "confirmed"].includes(job.status);

  /** Its questions are live in the bank, so the bank — not the draft — is the thing to open. */
  const isImportedToBank = (job) => job.status === "completed" && job.questions_imported > 0;

  const categoryOptions = useMemo(
    () =>
      [...new Set(imports.map((job) => normalizeCategoryName(job)).filter(Boolean))].sort((a, b) =>
        a.localeCompare(b)
      ),
    [imports]
  );

  const filteredImports = useMemo(() => {
    if (!categoryFilter) return imports;
    return imports.filter((job) => normalizeCategoryName(job) === categoryFilter);
  }, [imports, categoryFilter]);

  return (
    <Box sx={{ p: 3, maxWidth: 960, mx: "auto" }}>
      <Box sx={{ mb: 3, display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
            Recent Imports
          </Typography>
          <Typography variant="body2" color="text.secondary">
            View uploaded PDFs and open Question Bank review for any previous import.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          size="small"
          startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
          disabled={loading}
          onClick={fetchImports}
          sx={{ textTransform: "none" }}
        >
          {loading ? "Loading..." : "Refresh"}
        </Button>
      </Box>

      <Box sx={{ mb: 2, display: "flex", flexWrap: "wrap", gap: 2, alignItems: "flex-start", justifyContent: "space-between" }}>
        <TextField
          size="small"
          label="Search"
          placeholder="ID, filename, exam title, school, subject…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          sx={{ minWidth: 280, flex: "1 1 240px" }}
        />
        <FormControl size="small" sx={{ minWidth: 185 }}>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            displayEmpty
            sx={{ borderRadius: 2 }}
          >
            <MenuItem value="actionable">Status: ready &amp; done</MenuItem>
            <MenuItem value="preview">Ready for review</MenuItem>
            <MenuItem value="completed">Completed</MenuItem>
            <MenuItem value="confirmed">Confirmed</MenuItem>
            <MenuItem value="processing">Processing</MenuItem>
            <MenuItem value="failed">Failed</MenuItem>
            <MenuItem value="all">Status: all</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 185 }}>
          <Select
            value={subjectFilter}
            onChange={(e) => setSubjectFilter(e.target.value)}
            displayEmpty
            sx={{ borderRadius: 2 }}
          >
            <MenuItem value="">Subject: all</MenuItem>
            {subjects.map((s) => (
              <MenuItem key={s.key} value={s.key}>
                {s.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 260 }}>
          <InputLabel id="recent-imports-category-filter-label">Past paper category</InputLabel>
          <Select
            labelId="recent-imports-category-filter-label"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            label="Past paper category"
            sx={{ borderRadius: 2 }}
          >
            <MenuItem value="">All categories</MenuItem>
            {categoryOptions.map((categoryName) => (
              <MenuItem key={categoryName} value={categoryName}>
                {categoryName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {/* A–Z Letter Filter */}
      <Box sx={{ mb: 1.5, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
        {["All", ...Array.from({ length: 26 }, (_, i) => String.fromCharCode(65 + i))].map((letter) => {
          const active = letter === "All" ? !letterFilter : letterFilter === letter;
          return (
            <Chip
              key={letter}
              label={letter}
              size="small"
              variant={active ? "contained" : "outlined"}
              color={active ? "primary" : "default"}
              onClick={() => setLetterFilter(letter === "All" ? "" : letter)}
              sx={{ minWidth: 30, fontSize: "0.7rem", height: 22, cursor: "pointer" }}
            />
          );
        })}
      </Box>

      <Paper sx={{ borderRadius: 3, overflow: "hidden" }}>
        <Box sx={{ px: 2, py: 2, borderBottom: 1, borderColor: "divider", bgcolor: "grey.50", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
          <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
            All previous imports (PDF link + Open Question Bank)
          </Typography>
          {total > 0 && (
            <Typography variant="caption" color="text.secondary">
              {total} import{total !== 1 ? "s" : ""}
            </Typography>
          )}
        </Box>
        <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
          {loading && imports.length === 0 ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 4 }}>
              <CircularProgress size={24} />
              <Typography variant="body2" color="text.secondary">
                Loading recent imports...
              </Typography>
            </Box>
          ) : imports.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
              No import jobs found. Upload a PDF from Import from PDF to get started.
            </Typography>
          ) : filteredImports.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
              No imports match the selected past paper category.
            </Typography>
          ) : (
            filteredImports.map((job) => (
              <Paper
                key={job.id}
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 2,
                  display: "grid",
                  gridTemplateColumns: "minmax(0, 1fr) auto",
                  gridTemplateRows: "auto auto",
                  columnGap: 1.5,
                  rowGap: 0.5,
                }}
              >
                <Box
                  sx={{
                    gridColumn: "1 / -1",
                    gridRow: 1,
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1,
                    minWidth: 0,
                    width: "100%",
                  }}
                >
                  {job.school_logo && (
                    <Box
                      component="img"
                      src={job.school_logo}
                      alt="School logo"
                      sx={{
                        width: 28,
                        height: 28,
                        objectFit: "contain",
                        borderRadius: 0.5,
                        border: "1px solid #e0e0e0",
                        flexShrink: 0,
                        mt: 0.2,
                      }}
                    />
                  )}
                  <Tooltip title={job.source_filename || `Import #${job.id}`}>
                    <Box
                      sx={{
                        minWidth: 0,
                        flex: "1 1 0%",
                        overflowX: "auto",
                        overflowY: "hidden",
                      }}
                    >
                      <Typography
                        variant="body1"
                        component="div"
                        sx={{
                          fontWeight: 600,
                          whiteSpace: "nowrap",
                          width: "max-content",
                          maxWidth: "none",
                        }}
                      >
                        {job.source_filename || `Import #${job.id}`}
                      </Typography>
                    </Box>
                  </Tooltip>
                </Box>

                <Box
                  sx={{
                    gridColumn: 1,
                    gridRow: 2,
                    minWidth: 0,
                    display: "flex",
                    gap: 1,
                    alignItems: "center",
                    flexWrap: "wrap",
                  }}
                >
                  <Chip
                    label={getStatusLabel(job.status)}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: "0.7rem",
                      fontWeight: 600,
                      bgcolor: statusColors[job.status]?.bg || "#f5f5f5",
                      color: statusColors[job.status]?.color || "#333",
                    }}
                  />
                  {normalizeCategoryName(job) && (
                    <Chip
                      label={normalizeCategoryName(job)}
                      size="small"
                      variant="outlined"
                      sx={{ height: 20, fontSize: "0.7rem", fontWeight: 600 }}
                    />
                  )}
                  <Chip
                    label={`Import #${job.id}`}
                    size="small"
                    variant="outlined"
                    sx={{ height: 20, fontSize: "0.7rem", fontWeight: 600, fontFamily: "monospace" }}
                  />
                  {job.questions_imported > 0 && (
                    <Typography variant="caption" color="text.secondary">
                      {job.questions_imported} questions
                    </Typography>
                  )}
                  {job.created_at && (
                    <Typography variant="caption" color="text.secondary">
                      {format(new Date(job.created_at), "d MMM yyyy, HH:mm")}
                    </Typography>
                  )}
                </Box>

                <Box
                  sx={{
                    gridColumn: 2,
                    gridRow: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    flexShrink: 0,
                    justifySelf: "end",
                    alignSelf: "center",
                  }}
                >
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<PdfIcon />}
                    onClick={() => navigate(`/admin/question-bank/papers/${job.id}`)}
                    sx={{ textTransform: "none" }}
                    title="Open uploaded PDF"
                  >
                    View PDF
                  </Button>
                  {isImportedToBank(job) && job.has_mcq_conversion ? (
                    <Button
                      size="small"
                      variant="outlined"
                      color="success"
                      startIcon={<AutoAwesomeIcon />}
                      onClick={() => navigate(`/admin/question-bank/mcq-review?job=${job.mcq_job_id}`)}
                      sx={{ textTransform: "none" }}
                      title="This paper has already been converted to MCQs — open the review"
                    >
                      MCQ generated
                    </Button>
                  ) : (
                    isImportedToBank(job) && (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<AutoAwesomeIcon />}
                        onClick={() => setConvertTarget(job)}
                        sx={{ textTransform: "none" }}
                        title="Turn every question in this paper into a multiple-choice question"
                      >
                        Convert to MCQ
                      </Button>
                    )
                  )}
                  {canOpenQuestionBank(job) && (
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<LibraryBooksIcon />}
                      onClick={() =>
                        navigate(
                          isImportedToBank(job)
                            ? `/admin/question-bank?import_id=${job.id}`
                            : `/admin/question-bank/import/${job.id}/review`
                        )
                      }
                      sx={{ textTransform: "none" }}
                      title={
                        isImportedToBank(job)
                          ? "Open this paper's questions in the question bank"
                          : "Open Question Bank review for this import"
                      }
                    >
                      Open Question Bank
                    </Button>
                  )}
                </Box>
              </Paper>
            ))
          )}
        </Box>
        {lastPage > 1 && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2, borderTop: 1, borderColor: "divider" }}>
            <Pagination
              count={lastPage}
              page={page}
              onChange={(_, value) => setPage(value)}
              color="primary"
              size="small"
              showFirstButton
              showLastButton
            />
          </Box>
        )}
      </Paper>

      <Box sx={{ mt: 2, display: "flex", gap: 1, alignItems: "center" }}>
        <Button
          size="small"
          startIcon={<OpenInNewIcon />}
          onClick={() => navigate("/admin/question-bank/import")}
          sx={{ textTransform: "none" }}
        >
          Import from PDF
        </Button>
        <Typography variant="caption" color="text.secondary">
          Upload a new PDF to create another import.
        </Typography>
      </Box>

      <ConvertToMcqDialog
        open={Boolean(convertTarget)}
        onClose={() => setConvertTarget(null)}
        importId={convertTarget?.id}
        title={convertTarget?.source_filename}
        questionCount={convertTarget?.questions_imported}
        defaultCategoryId={convertTarget?.target_category_id || ""}
      />
    </Box>
  );
};

export default QuestionBankRecentImports;
